import React, { useEffect, useState, useRef } from 'react';
import { Divider, Row, Col, Space, Form, Select, Input, Button, Table, DatePicker, Modal } from 'antd';
import { PlusSquareFilled, SearchOutlined } from '@ant-design/icons';
import { useJsApiLoader, GoogleMap, LoadScript, Marker, DirectionsService, DirectionsRenderer, TrafficLayer, InfoWindow } from '@react-google-maps/api';
import { Link } from 'react-router-dom';
import { withAlert } from 'react-alert';
import { compose } from 'redux';
import moment from 'moment';
import _ from 'lodash';
import config from '../../config';

import ModalMap from './components/ModalMap';

const { Option } = Select;
const { TextArea } = Input;

const FavoriteDestinationListPage = props => {
	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: config.google_api_key,
		libraries: ['places']
	});

	const prevPropsRef = useRef();

	const { getFavoriteDestinations, createFavoriteDestinations, deleteFavoriteDestinationById, pageProps, resetPage, resetMeta } = props;
	const { favorite_destinations, loading, loadingSubmit } = pageProps;

	const [windowSize, setWindowSize] = useState(window.innerWidth);
	const [selectedId, setSelectedId] = useState(null);
	const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);

	//	Modal Map States
	const [isModalMapOpen, setIsModalMapOpen] = useState(false);
	const [selectedAddress, setSelectedAddress] = useState(null);
	const [currentMarked, setCurrentMarked] = useState(null);

	useEffect(() => {
		getFavoriteDestinations();

		const handleResize = () => setWindowSize(window.innerWidth);

		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
			resetPage();
		};
	}, [getFavoriteDestinations, resetPage]);

	// Check prevProps vs currentProps
	useEffect(() => {
		if (pageProps && pageProps.meta) {
			const { meta } = pageProps;

			// Show an alert when the component first mounts
			if (!prevPropsRef.current) {
				const alertOptions = {
					type: meta.code === 200 ? 'success' : 'error',
					...(meta.code === 40399 && { timeout: 0 })
				};
				props.alert.show(meta.message, alertOptions);
				resetMeta();

				if (meta.code === 200) {
					setSelectedAddress(null);
					setCurrentMarked(null);
					setSelectedId(null);
					setIsModalMapOpen(false);

					if (isModalDeleteOpen) {
						setIsModalDeleteOpen(false);
					}
				}
			}

			// Check if the meta object has changed
			if (prevPropsRef.current && prevPropsRef.current.meta !== meta) {
				const alertOptions = {
					type: meta.code === 200 ? 'success' : 'error',
					...(meta.code === 40399 && { timeout: 0 })
				};
				props.alert.show(meta.message, alertOptions);
				resetMeta();

				if (meta.code === 200) {
					setSelectedAddress(null);
					setCurrentMarked(null);
					setSelectedId(null);
					setIsModalMapOpen(false);

					if (isModalDeleteOpen) {
						setIsModalDeleteOpen(false);
					}
				}
			}

			// Save the current props for the next update
			prevPropsRef.current = pageProps;
		}
	}, [isModalDeleteOpen, pageProps, props, resetMeta]);

	const handleConfirm = () => {
		const fields = {
			name: selectedAddress,
			...currentMarked
		};

		selectedAddress && currentMarked && createFavoriteDestinations(fields);
	};

	const renderModalDelete = () => {
		const filteredLocation = favorite_destinations.find(item => item._id === selectedId);

		return (
			<Modal
				closable={false}
				open={isModalDeleteOpen}
				title={filteredLocation ? `Delete Location: ${filteredLocation.name}` : ''}
				onCancel={() => {
					setIsModalDeleteOpen(false);
					setSelectedId(null);
				}}
				centered={true}
				width={350}
				footer={null}
			>
				<Row>Are you sure you want to delete this location?</Row>
				<Row>
					<div style={{ width: '100%', marginTop: 30, display: 'flex', justifyContent: 'space-between' }}>
						<Button
							type="primary"
							danger
							style={{ width: 120 }}
							onClick={() => {
								deleteFavoriteDestinationById(selectedId);
								// setIsModalDeleteOpen(false);
							}}
							loading={loadingSubmit}
						>
							Confirm
						</Button>
						<Button
							type="default"
							style={{ width: 120 }}
							onClick={() => {
								setIsModalDeleteOpen(false);
								setSelectedId(null);
							}}
							loading={loadingSubmit}
						>
							Cancel
						</Button>
					</div>
				</Row>
			</Modal>
		);
	};

	const columns = [
		{
			title: 'Location Name',
			dataIndex: 'name',
			key: 'name'
			// width: '20%'
		},
		{
			title: 'Latitude',
			dataIndex: 'latitude',
			key: 'latitude'
			// render: latitude => <>{date_of_trip && moment(date_of_trip).format('YYYY-MM-DD')}</>,
			// width: '10%'
		},
		{
			title: 'Longitude',
			dataIndex: 'longitude',
			key: 'longitude'
			// render: latitude => <>{date_of_trip && moment(date_of_trip).format('YYYY-MM-DD')}</>,
			// width: '10%'
		},

		{
			title: 'Action',
			dataIndex: '_id',
			key: '_id',
			render: _id => {
				return (
					<Space size="middle">
						<Link
							to="#"
							onClick={e => {
								e.preventDefault();
								setSelectedId(_id);
								setIsModalDeleteOpen(true);
							}}
						>
							Delete
						</Link>
					</Space>
				);
			}
			// width: '10%'
		}
	];

	return (
		<React.Fragment>
			{renderModalDelete()}
			<ModalMap
				isLoaded={isLoaded}
				alert={props.alert}
				isModalMapOpen={isModalMapOpen}
				setIsModalMapOpen={setIsModalMapOpen}
				setCurrentMarked={setCurrentMarked}
				currentMarked={currentMarked}
				setSelectedAddress={setSelectedAddress}
				selectedAddress={selectedAddress}
				windowSize={windowSize}
				handleConfirm={handleConfirm}
				loadingSubmit={loadingSubmit}
			/>
			<Row>
				<Col
					span={22}
					offset={1}
				>
					<div className="main-content">
						<Row>
							<Col
								lg={{ span: 11, offset: 1 }}
								md={{ span: 11, offset: 1 }}
								sm={{ span: 23, offset: 1 }}
								xs={{ span: 24 }}
							>
								<div>
									<h1 style={{ color: '#003366' }}>Favorite Locations</h1>
								</div>
							</Col>
						</Row>
						<Row
							align="middle"
							justify="end"
							style={{ marginBottom: 18 }}
						>
							<Col
								lg={{ span: 12 }}
								sm={{ span: 12 }}
								xs={{ span: 12 }}
							>
								<Button
									type="primary"
									icon={<PlusSquareFilled />}
									style={{ marginLeft: 10, float: 'right' }}
									onClick={() => setIsModalMapOpen(true)}
								>
									Add Favorite Location
								</Button>
								<Button
									style={{ color: '#0088FF', borderColor: '#0088FF', float: 'right', marginTop: windowSize < 794 ? 10 : 0 }}
									onClick={() => props.history.push('/applications')}
								>
									Booking List
								</Button>
							</Col>
						</Row>

						{
							<Row>
								<Col span={24}>
									<Table
										loading={loading}
										dataSource={favorite_destinations}
										columns={columns}
										pagination={{
											position: ['bottomRight'],
											showSizeChanger: true,
											total: favorite_destinations.length
										}}
										scroll={{
											x: 1000
										}}
									/>
								</Col>
							</Row>
						}
					</div>
				</Col>
			</Row>
		</React.Fragment>
	);
};

export default compose(withAlert())(FavoriteDestinationListPage);
